import { memo, useRef, ChangeEvent, useEffect } from 'react';
import { useField, useFormikContext } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useLocale } from '../../../../context/Localization/localization';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { ZipCodePatternMask } from '../ZipCodePatternMask/ZipCodePatternMask';
import { WebFormZipCodeFieldProps } from '../FormZipCodeField.types';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const WebFormZipCodeField = memo(({
    name,
    id,
    title,
    label,
    ariaLabel,
    disabled,
    required,
    autoFocus,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
    tabFocusRefContext,
    onChangeCallback,
}: WebFormZipCodeFieldProps) => {
    const [field, meta] = useField({
        name: name,
        validate: TextFieldValidation({
            required: required ?? false,
            label,
            name,
            validationType: 'zipCode',
        }),
    });
    const { submitCount, setFieldValue } = useFormikContext();

    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const {
        dealer: { primaryLanguage },
    } = useKioskState();
    const locale = useLocale();

    useEffect(() => {
        if (tabFocusRefContext) {
            tabFocusRefContext.current = inputRef.current;
        }
    }, [tabFocusRefContext]);

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFieldValue(name, event.target.value);
        onChangeCallback && onChangeCallback(event);
    };

    return (
        <BaseTextField
            name={name}
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            inputMode={'numeric'}
            type={'text'}
            inputRef={inputRef}
            value={field.value ?? ''}
            error={meta.error}
            touched={meta.touched || submitCount > 0}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            capsLock={capsLock}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            onChange={handleOnChange}
            safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            inputProps={{
                maxLength: 5,
            }}
            InputProps={{
                inputComponent: ZipCodePatternMask as any,
            }}
        />
    );
});
